import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FieldChangedMark from 'components/FieldChangedMark/FieldChangedMark';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import {
  type UseDimensionsImperativeApi,
  useTableDimensions,
} from 'hooks/useDimensions';
import {
  type TColumns,
  type UnknownObject,
  usePersistedColumnsModal,
} from 'hooks/useSortColumnsModal';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import isNumber from 'lodash/isNumber';
import type { IAPIResult } from 'types/state.interface';
import {
  Button,
  type Column,
  EditableTable,
  type EditableTableProps,
  type GetMore,
  type GetMoreOptions,
  useInfiniteScroll,
} from '@timeedit/ui-components';
import {
  Flex,
  type MenuProps,
  type TableProps,
} from '@timeedit/ui-components/antd';
import type {
  ItemType,
  MenuDividerType,
} from '@timeedit/ui-components/antd/menu/interface';

export const ITEMS_PER_PAGE = 200;
const TABLE_ROW_SELECTOR = '.te-table-row';
const DEFAULT_TABLE_ROW_HEIGHT = 50;
const TABLE_HEIGHT_MARGIN = 6;

type ColumnKey = keyof any;

export type ContextMenuAction<T extends object = UnknownObject> = ItemType & {
  /** Callback when the action is clicked */
  onActionClick?: (object?: T) => void;
};

export type ContextMenuActions<T extends object = UnknownObject> = {
  /** Label for the item in the context menu */
  itemLabel?: string | ((item: T) => string);

  /** Actions to show in the context menu */
  actions: ContextMenuAction<T>[];
};

export type AdminTableProps<
  K extends ColumnKey = string,
  T extends { id: string | number } = { id: string | number },
  P extends object = UnknownObject,
> = Omit<TableProps<T>, 'dataSource' | 'columns'> & {
  /** Unique identifier for table, used for persisted config */
  tableId: string;

  /** The columns to display in the table */
  columns: TColumns<K, T>;

  /** The items to display in the table */
  items: T[];

  /** Actions to show in the context menu */
  contextMenuActions?: ContextMenuActions<T>;

  /** Content to show in the footer */
  footerContent?: React.ReactNode[];

  /** Callback for row actions */
  onRow?: EditableTableProps<T>['onRow'];

  /** Function to fetch items */
  fetchFn: (options: P) => Promise<IAPIResult<T>>;

  /** Total item count, can be a number or a function */
  totalItemCount?: number | (() => Promise<number>);

  /** Whether the table rows are selectable */
  selectable?: boolean;

  /** IDs of objects that have been added and should be highlighted */
  changedIds?: string[];

  /** The total amount of items based on the current filter */
  onFilteredTotalChanged?: (count: number) => void;

  /**
   * Override the "filtered amount" shown in the title. By default, the table tracks the value
   * returned from the latest `fetchFn` via `useInfiniteScroll`, which is correct for server-only
   * lists but goes stale when the displayed count is also driven by local state (e.g. pending
   * adds/removes). Pass a number here to take ownership of that value.
   */
  filteredTotalOverride?: number;

  objectTypeId: number;
};

const useInfiniteScrollAdminTable = <T extends object = UnknownObject>(
  items: T[],
  fetchFn: (options: GetMoreOptions) => Promise<IAPIResult<T>>,
  tableRef: React.RefObject<Element>,
  apiRef?: React.RefObject<UseDimensionsImperativeApi>
) => {
  const [isInitialFetch, setIsInitialFetch] = useState(true);

  const { height } = useTableDimensions(tableRef, apiRef);

  const getMore = useCallback<GetMore<T>>(
    async (options) => {
      setIsInitialFetch(false);

      const response = await fetchFn(options);

      const { results, page, totalPages, totalResults } = response;

      return {
        data: results,
        page,
        count: results.length,
        total: totalResults,
        hasMore: page < totalPages,
      };
    },
    [fetchFn]
  );

  const tableRowHeight = useMemo(
    () =>
      document.querySelector(TABLE_ROW_SELECTOR)?.clientHeight ??
      DEFAULT_TABLE_ROW_HEIGHT,
    []
  );

  const useInfiniteScrollResult = useInfiniteScroll<T>({
    itemHeight: tableRowHeight,
    containerHeight: height,
    getMore,
    limit: ITEMS_PER_PAGE,
    virtual: true,
    initialFetch: isInitialFetch,
  });

  useEffect(() => {
    // Reset scroll position when items are reset and the getMore function has changed, indicating that new data should be fetched
    if (items.length === 0) {
      useInfiniteScrollResult.reset();
      setIsInitialFetch(true);
    }
  }, [useInfiniteScrollResult.reset, getMore, items.length]);

  return { ...useInfiniteScrollResult, height };
};

const useFooter = (footerContent?: React.ReactNode[]) =>
  useMemo(
    (): EditableTableProps['footer'] => () => {
      if (!footerContent || footerContent.length === 0) {
        return null;
      }

      return (
        <Flex className="tw:gap-2">
          {footerContent.map((action, index) => (
            <Flex key={index}>{action}</Flex>
          ))}
        </Flex>
      );
    },
    [footerContent]
  );

const useContextMenu = <T extends object = UnknownObject>(
  contextMenuActions?: ContextMenuActions<T>
) =>
  useCallback(
    (item: T): MenuProps => {
      if (
        contextMenuActions == null ||
        contextMenuActions.actions.length === 0
      ) {
        return null;
      }

      const label =
        typeof contextMenuActions.itemLabel === 'function'
          ? contextMenuActions.itemLabel(item)
          : contextMenuActions.itemLabel;

      // Only show the label and divider if a label is provided
      const labelAndDividerItems: ItemType[] =
        label != null
          ? [
              {
                key: 'title',
                label,
                disabled: true,
                style: { cursor: 'auto' },
              },
              { type: 'divider' } as MenuDividerType,
            ]
          : [];

      return {
        items: [...labelAndDividerItems, ...(contextMenuActions.actions ?? [])],
        onClick: (menuInfo) => {
          menuInfo.domEvent.stopPropagation();

          const action = contextMenuActions.actions.find(
            (action) => action.key === menuInfo.key
          );

          action?.onActionClick(item);
        },
      };
    },
    [contextMenuActions]
  );

/**  If there are dirty fields, modify the first column to include the dirty indicator */
function useMarkAddedRows<
  T extends { id: string | number } = { id: string | number },
>(columns: Column<T>[], addedIds: string[]) {
  const columnsWithDirtyMarking = useMemo(() => {
    if (!columns.length) return columns;
    const firstColumn = columns[0];
    if (firstColumn && addedIds?.length) {
      const oldRender = firstColumn.render;
      const newFirstColumn = {
        ...firstColumn,
        render: (value: string, record, i) => (
          <FieldChangedMark
            changed={addedIds?.includes(record.id.toString())}
            value={oldRender ? oldRender(value, record, i) : value}
          />
        ),
      };
      return [newFirstColumn, ...columns.slice(1)];
    }
    return columns;
  }, [columns, addedIds]);

  return columnsWithDirtyMarking;
}

/**
 * General table component for displaying and managing items in Admin.
 */
export const AdminTable = <
  K extends ColumnKey,
  T extends { id: string | number } = { id: string | number },
>({
  tableId,
  columns,
  items,
  contextMenuActions,
  footerContent,
  onRow,
  fetchFn,
  totalItemCount,
  loading,
  changedIds,
  onFilteredTotalChanged,
  filteredTotalOverride,
  objectTypeId,
  ...props
}: AdminTableProps<K, T>) => {
  const [totalCount, setTotalCount] = useState<number>(0);
  const apiRef = React.useRef<UseDimensionsImperativeApi>(null);

  useEffect(() => {
    if (isNumber(totalItemCount)) {
      setTotalCount(totalItemCount);
      return;
    }

    if (typeof totalItemCount === 'function') {
      totalItemCount().then((count) => {
        setTotalCount(count);
      });
      return;
    }

    setTotalCount(0);
  }, [totalItemCount]);

  const getMenuProps = useContextMenu<T>(contextMenuActions);

  const [contentRef, defaultTableProps] =
    useDefaultTableProps<T>(TABLE_HEIGHT_MARGIN);

  const {
    onScroll,
    loading: isScrollLoading,
    total: filteredTotal,
    height,
  } = useInfiniteScrollAdminTable(items, fetchFn, contentRef, apiRef);

  useEffect(() => {
    onFilteredTotalChanged?.(filteredTotal);
  }, [filteredTotal, onFilteredTotalChanged]);

  const { sortColumnsModal, openSortColumnsModal, sortedColumns } =
    usePersistedColumnsModal<ColumnKey, T>(columns, tableId, objectTypeId);

  const titleActions = useMemo(
    () => ({
      right: (
        <Button onClick={openSortColumnsModal} size="small">
          {getText('ui.table.edit_columns')}
        </Button>
      ),
    }),
    [openSortColumnsModal]
  );

  const columnsWithDirtyMarking = useMarkAddedRows(
    sortedColumns,
    changedIds ?? []
  );

  const title: EditableTableProps['title'] = useTableTitle<T>({
    total: totalCount,
    filteredAmount: filteredTotalOverride ?? filteredTotal,
    actions: titleActions,
  });

  const footer: EditableTableProps['footer'] = useFooter(footerContent);

  const onColumnSizeChange = useCallback(() => {
    apiRef.current?.resizeTable();
  }, []);

  return (
    <Flex flex={1} ref={contentRef} style={{ height: height }}>
      <EditableTable<T>
        {...defaultTableProps}
        {...props}
        columns={columnsWithDirtyMarking}
        dataSource={items}
        fitColumns={false}
        title={title}
        onScroll={onScroll}
        onColumnSizeChange={onColumnSizeChange}
        // Ugly way to make sure that the footer isn't rendered as an empty footer when no content is provided
        {...((footerContent?.length ?? 0 > 0) ? { footer: footer } : {})}
        loading={loading || isScrollLoading}
        getMenuProps={getMenuProps}
        onRow={onRow}
      />
      {sortColumnsModal}
    </Flex>
  );
};
